import { css } from "@emotion/react";
import Stack from "@mui/material/Stack";
import type { TextFieldProps } from "@mui/material/TextField";
import TextField from "@mui/material/TextField";
import { theme } from "@polifonia/theme";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Typography } from "@/components/Typography";
import { Trans } from "@/features/i18n/Trans";

const cssFieldLabel = css`
  color: ${theme.colors.staticFgPrimary};
`;

const cssDescription = css`
  color: ${theme.colors.staticFgNote};
`;

type TranslationPrefix = "fileUpload" | "urlFeed";

interface FeedNameFieldProps extends Pick<
  TextFieldProps,
  "value" | "disabled"
> {
  onChange: (value: string) => void;
  translationPrefix?: TranslationPrefix;
}

const i18nRecord: Record<
  TranslationPrefix,
  {
    feedName: ReactNode;
    feedNamePlaceholder: string;
    feedNameHint: ReactNode;
  }
> = {
  fileUpload: {
    feedName: <Trans i18nKey="productFeed.fileUpload.feedName" />,
    feedNamePlaceholder: "productFeed.fileUpload.feedNamePlaceholder",
    feedNameHint: <Trans i18nKey="productFeed.fileUpload.feedNameHint" />,
  },
  urlFeed: {
    feedName: <Trans i18nKey="productFeed.urlFeed.feedName" />,
    feedNamePlaceholder: "productFeed.urlFeed.feedNamePlaceholder",
    feedNameHint: <Trans i18nKey="productFeed.urlFeed.feedNameHint" />,
  },
};

export const FeedNameField: React.FC<FeedNameFieldProps> =
  function FeedNameField({
    value,
    onChange,
    disabled = false,
    translationPrefix = "fileUpload",
  }) {
    const { t } = useTranslation();
    const i18n = i18nRecord[translationPrefix];

    return (
      <Stack direction="column" gap={1}>
        <Typography variant="body" css={cssFieldLabel}>
          {i18n.feedName}
        </Typography>
        <TextField
          placeholder={t(i18n.feedNamePlaceholder)}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          required
          disabled={disabled}
        />
        <Typography variant="body" css={cssDescription}>
          {i18n.feedNameHint}
        </Typography>
      </Stack>
    );
  };
